import React from 'react';
import Layout from './Layout';
import { TrendingDown, DollarSign, Globe, Award } from 'lucide-react';

const ClimateMetrics: React.FC = () => {
  const compensationMetrics = [
    {
      title: 'Total CO₂ Avoided',
      value: '12,847 tCO₂e',
      target: '15,000 tCO₂e',
      progress: 85.6,
      icon: TrendingDown,
      color: 'text-green-600'
    },
    {
      title: 'Climate Finance Generated',
      value: '$2.4M',
      target: '$3.0M',
      progress: 80.0,
      icon: DollarSign,
      color: 'text-blue-600'
    },
    {
      title: 'Article 6 Credits',
      value: '8,234 ITMOs',
      target: '10,000 ITMOs',
      progress: 82.3,
      icon: Globe,
      color: 'text-purple-600'
    },
    {
      title: 'Gold Standard Credits',
      value: '4,567 VERs',
      target: '5,000 VERs',
      progress: 91.3,
      icon: Award,
      color: 'text-yellow-600'
    }
  ];

  const partnerships = [
    {
      country: 'Sweden',
      type: 'Article 6 Partnership',
      status: 'Active',
      credits: '2,847 ITMOs',
      value: '$568K'
    },
    {
      country: 'Germany',
      type: 'Climate Finance',
      status: 'Negotiating',
      credits: '1,234 VERs',
      value: '$247K'
    },
    {
      country: 'South Africa',
      type: 'South-South Cooperation',
      status: 'Active',
      credits: '3,456 ITMOs',
      value: '$692K'
    },
    {
      country: 'Kenya',
      type: 'Just Transition Program',
      status: 'Planning',
      credits: '567 VERs',
      value: '$113K'
    }
  ];

  const sectorImpacts = [
    {
      sector: 'Steel Production',
      coalReduction: '45%',
      co2Avoided: '7,234 tCO₂e',
      biogasUsage: '1,234 MWh',
      financialImpact: '$1.4M'
    },
    {
      sector: 'Cement Manufacturing',
      coalReduction: '32%',
      co2Avoided: '5,613 tCO₂e',
      biogasUsage: '892 MWh',
      financialImpact: '$1.0M'
    }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Climate Compensation Metrics</h1>
          <div className="text-sm text-gray-500">
            COP30 Compliant | Gold Standard Verified
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {compensationMetrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${metric.color}`} />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Target: {metric.target}</span>
                    <span className="font-medium">{metric.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${metric.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">International Partnerships</h2>
            <div className="space-y-4">
              {partnerships.map((partnership, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-gray-900">{partnership.country}</h3>
                      <p className="text-sm text-gray-600">{partnership.type}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      partnership.status === 'Active' ? 'bg-green-100 text-green-800' :
                      partnership.status === 'Negotiating' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {partnership.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Credits: {partnership.credits}</span>
                    <span className="font-medium text-green-600">{partnership.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sector Impact Analysis</h2>
            <div className="space-y-6">
              {sectorImpacts.map((sector, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">{sector.sector}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Coal Reduction:</span>
                      <span className="ml-2 font-medium text-red-600">{sector.coalReduction}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">CO₂ Avoided:</span>
                      <span className="ml-2 font-medium text-green-600">{sector.co2Avoided}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Biogas Usage:</span>
                      <span className="ml-2 font-medium text-blue-600">{sector.biogasUsage}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Financial Impact:</span>
                      <span className="ml-2 font-medium text-purple-600">{sector.financialImpact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Mitigation Outcomes Timeline</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-900">Q4 2024 - Steel Plant A Milestone</h3>
                <p className="text-sm text-green-700">Achieved 2,847 tCO₂e reduction through biogas integration</p>
                <p className="text-xs text-green-600">Generated 1,234 Article 6 ITMOs for Sweden partnership</p>
              </div>
              <div className="text-sm font-medium text-green-600">$568K</div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900">Q3 2024 - Cement Plant B Achievement</h3>
                <p className="text-sm text-blue-700">Circular waste pathway reduced emissions by 1,892 tCO₂e</p>
                <p className="text-xs text-blue-600">Gold Standard verification completed for 892 VERs</p>
              </div>
              <div className="text-sm font-medium text-blue-600">$178K</div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-purple-900">Q2 2024 - Multi-Country Program Launch</h3>
                <p className="text-sm text-purple-700">South-South cooperation program initiated with Kenya and South Africa</p>
                <p className="text-xs text-purple-600">Just Transition methodology approved for steel sector</p>
              </div>
              <div className="text-sm font-medium text-purple-600">$805K</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Status</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">COP30 Integrity</span>
                <span className="font-semibold text-green-600">✓ Compliant</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gold Standard</span>
                <span className="font-semibold text-green-600">✓ Verified</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Article 6 Ready</span>
                <span className="font-semibold text-blue-600">✓ TRL 5-6</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Market Opportunities</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">EU Green Steel</span>
                <span className="font-semibold text-green-600">$1.2M</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">African Markets</span>
                <span className="font-semibold text-blue-600">$800K</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Climate Finance</span>
                <span className="font-semibold text-purple-600">$400K</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Innovation Impact</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Technology Readiness</span>
                <span className="font-semibold">TRL 5-6</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pilot Ready</span>
                <span className="font-semibold text-green-600">✓ Yes</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Scale Potential</span>
                <span className="font-semibold text-blue-600">High</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClimateMetrics;